import { loadYouTubeVideo, setupYouTubeStateChangeListener, preloadYouTubeIframe } from './youtube';
import { addVideoEventListeners } from './videoPlayer';

/**
 * Handle the play button click event for YouTube videos.
 *
 * @param {HTMLElement} videoWrapper   The wrapper element containing the video.
 * @param {string} youtubeVideoUrl     The YouTube video URL.
 * @param {Object} options             The options for the video.
 * @param {boolean} options.autoplay   Whether the video should autoplay.
 * @param {boolean} options.muted      Whether the video should be muted.
 * @param {boolean} options.loop       Whether the video should loop.
 * @param {boolean} options.controls   Whether the video should show controls.
 */
export function handleYouTubePlayButton(videoWrapper, youtubeVideoUrl, { autoplay, muted, loop, controls }) {
  const playButton = videoWrapper.querySelector('.pr-video__play-button');
  const iframeWrapper = videoWrapper.querySelector('.pr-video__wrapper');

  if (!playButton) {
    return;
  }

  const updateVideoState = (state) => {
    videoWrapper.setAttribute('data-video-state', state);
  };

  // Preload the iframe
  preloadYouTubeIframe(iframeWrapper, youtubeVideoUrl, { autoplay, muted, loop, controls });

  playButton.addEventListener('click', () => {
    const iframe = iframeWrapper.querySelector('iframe');
    if (iframe) {
      setupYouTubeStateChangeListener(iframe, videoWrapper);
      iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
      updateVideoState('playing');
    }
  });

  window.addEventListener('message', (event) => {
    if (event.origin === 'https://www.youtube.com' && event.data) {
      const messageData = JSON.parse(event.data);

      if (messageData.event === 'onStateChange') {
        if (messageData.info === 0) {
          updateVideoState('ended');
        } else if (messageData.info === 1) {
          updateVideoState('playing');
        } else if (messageData.info === 2) {
          updateVideoState('paused');
        }
      }
    }
  });
}

/**
 * Handles the play button functionality for URL videos.
 *
 * @param {HTMLElement} videoWrapper - The video wrapper element containing the video elements.
 */
export function handleURLVideoPlayButton(videoWrapper) {
  const playButton = videoWrapper.querySelector('.pr-video__play-button');

  if (playButton) {
    playButton.addEventListener('click', () => {
      const $videoElement = videoWrapper.querySelector('video');

      if ($videoElement) {
        $videoElement.play();
        addVideoEventListeners($videoElement, videoWrapper);
      } else {
        console.error('No video element found within videoWrapper', videoWrapper);
      }
    });
  }
}

/**
 * Handles the mute button functionality for URL videos.
 *
 * @param {HTMLElement} videoWrapper - The video wrapper element containing the video elements.
 */
export function handleURLVideoMuteButton(videoWrapper) {
  const muteButton = videoWrapper.querySelector('.pr-video__mute-button');

  if (muteButton) {
    muteButton.addEventListener('click', () => {
      const $videoElement = videoWrapper.querySelector('video');

      if ($videoElement) {
        $videoElement.muted = !$videoElement.muted;  // Toggle the mute state of the video

        // Directly toggle the 'video-muted' class based on the muted state of the video
        if ($videoElement.muted) {
          videoWrapper.classList.add('pr-video--muted');
        } else {
          videoWrapper.classList.remove('pr-video--muted');
        }
      } else {
        console.error('No video element found within videoWrapper', videoWrapper);
      }
    });
  }
}

/**
 * Handles the play/pause button functionality for URL videos.
 *
 * @param {HTMLElement} videoWrapper - The video wrapper element containing the video element.
 */
export function handleURLVideoPlayPauseButton( videoWrapper ) {
  const playPauseButton = videoWrapper.querySelector(
    '.pr-video__playpause-button'
  );

  if ( ! playPauseButton ) {
    return;
  }

  playPauseButton.addEventListener( 'click', () => {
    const videoEl = videoWrapper.querySelector( 'video' );

    if ( ! videoEl ) {
      console.error(
        'No video element found within videoWrapper',
        videoWrapper
      );
      return;
    }

    // Toggle play / pause
    if ( videoEl.paused ) {
      videoEl.play();
    } else {
      videoEl.pause();
    }
  } );
}
